import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class LoanApiService {
  private logoutUrl = environment.apiUrl + 'api/loan-review/logout';
  private apiUrl =
    environment.apiUrl + 'loan-review/v1/get-full-taxonomy-loan-file-review';

  private token = '';
  setToken(token: string) {
    this.token = token;
  }
  clearToken() {
    this.token = '';
  }

  constructor(private http: HttpClient) {}
  /**
   * Calls backend logout endpoint to invalidate the token.
   * @param token The JWT token to be invalidated
   */
  logout(token: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const logoutReview$ = this.http.post(this.logoutUrl, {}, { headers });
    return logoutReview$;
  }

  getProductList(): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    return this.http.get(this.apiUrl, { headers });
  }

  private _getAllDoc =
    environment.apiUrl + 'loan-review/v1/get-all-data-elements';

  /**
   * Fetch all documents. If optionalParams is provided it will be sent as
   * query parameters (usecase/product/sub_product/asset_class/data_element).
   * Many callers expect a simple GET with no params; however some backends
   * require the query params and previously caused errors when called without.
   */
  getAllDocument(optionalParams?: any): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });

    const options: any = { headers };
    if (optionalParams && Object.keys(optionalParams).length > 0) {
      // Normalize param names used by this endpoint
      const reqParams: any = {
        usecase: optionalParams.UseCase || optionalParams.usecase || 'Loan File Review',
        product: optionalParams.product || 'Loan',
        sub_product: optionalParams.sub_product || optionalParams.subproduct || optionalParams.subProduct,
        asset_class: optionalParams.asset_class || optionalParams.assetClass || optionalParams.asset_class_name,
        data_element: optionalParams.data_element || optionalParams.dataElement,
      };
      options.params = reqParams;
    }

    return this.http.get(this._getAllDoc, options);
  }

  getDocumentsForDataElement(param: any): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });

    const reqParams: any = {
      usecase: param?.UseCase || param?.usecase || 'Loan File Review',
      product: param?.product || 'Loan',
      sub_product: param?.sub_product,
      asset_class: param?.asset_class,
      data_element: param?.data_element,
    };

    return this.http.get(this._getAllDoc, { headers, params: reqParams });
  }

  private docuCategaory =
    environment.apiUrl + 'loan-review/v1/get-document-priorities';

  getDocumentCategory(param: any): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.useCase, // useCase,
      product: 'Loan', // param.source._value?.product,
      sub_product: param.source._value?.sub_product,
      asset_class: param.source._value?.asset_class,
      data_element: param.source._value?.data_element,
    };
    return this.http.get(this.docuCategaory, { headers, params: reqParams });
  }

  private _docByCat =
    environment.apiUrl + 'loan-review/v1/get-all-versions-by-document-category';

  getDocumentsByCategory(param: any): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });

    const reqParams = {
      usecase: 'Loan File Review', // useCase,
      product: 'Loan', // param.source._value?.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      document_category: param.DocumentCategory,
    };
    return this.http.get(this._docByCat, { headers, params: reqParams });
  }

  private _instructionStep =
    environment.apiUrl + 'loan-review/v1/get-instruction-step-by-version';

  getInstructionSteps(param: any): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: 'Loan File Review', // useCase,
      product: 'Loan', // param.source._value?.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      document_category: param.DocumentCategory,
      version: param.version,
    };
    return this.http.get(this._instructionStep, { headers, params: reqParams });
  }
  private updateDocPriorityUrl =
    environment.apiUrl + 'loan-review/v1/update-document-priority';

  updateDocumentPriority(param: any, body: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.UseCase,
      product: param.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
    };
    return this.http.put(this.updateDocPriorityUrl, body, {
      headers,
      params: reqParams,
    });
  }

  private addNewInstructionsUrl =
    environment.apiUrl + 'loan-review/v1/add-new-instructions';

  addNewInstructions(
    param: any,
    doc_category: string,
    priority: string,
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.UseCase,
      product: param.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      doc_category: doc_category,
      doc_priority: priority,
    };
    return this.http.post(
      this.addNewInstructionsUrl,
      { doc_priority: priority },
      {
        headers,
        params: reqParams,
      }
    );
  }

  private _docLabels =
    environment.apiUrl + 'loan-review/v1/loan-file-review/doc-labels';

  getDocumentLabels(): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: 'Loan File Review',
    };
    return this.http.get(this._docLabels, { headers, params: reqParams });
  }

  private addNewDataElementUrl =
    environment.apiUrl + 'loan-review/v1/add-new-data-element';

  addNewDataElement(
    param: any,
    userInputs: any
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams: any = {
      usecase: param.UseCase,
      product: param.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element_name: userInputs && userInputs['dataElementName'],
      level: userInputs && userInputs['selectLabel'],
      data_type: userInputs && userInputs['dataType'],
      report_type: userInputs && userInputs['tabName'],
      section: userInputs && userInputs['sectionName'],
    };
    return this.http.post(
      this.addNewDataElementUrl,
      userInputs,
      {
        headers,
        params: reqParams,
      }
    );
  }

  private updateDataElementMetadataUrl =
    environment.apiUrl + 'loan-review/v1/update-data-element-metadata';

  updateDataElementMetadata(param: any, userInputs: any, updatePayload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });

    const reqParams: any = {
      usecase: param.UseCase,
      product: param.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: userInputs && userInputs['dataElementName'],
    };
    const bodyToSend = updatePayload ?? userInputs;

    return this.http.put(this.updateDataElementMetadataUrl, bodyToSend, {
      headers,
      params: reqParams,
    });
  }

  private updateVersionStepUrl =
    environment.apiUrl + 'loan-review/v1/update-instruction-steps';

  // Extract missing query params and add inside reqParams
  // The original URL had: usecase, product, sub_product, asset_class, data_element, doc_category, version, version_is_latest, version_tags
  // Add version, version_is_latest, version_tags to reqParams

  updateVersionStep(
    param: any,
    userInputs: any,
    stepDetails: any[],
    TagList: string[]
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.UseCase,
      product: 'Loan',
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      doc_category: param.DocumentCategory,
      version: userInputs.Version,
      version_is_latest: userInputs.isLatest ?? true,
      version_tags: TagList.join(','),
    };
    return this.http.put(this.updateVersionStepUrl, stepDetails, {
      headers,
      params: reqParams,
    });
  }

  /* Add Version Step From Modal Add version */
  private addVersionUrl =
    environment.apiUrl +
    'loan-review/v1/add-new-version-with-instruction-steps';

  addVersionStep(
    param: any,
    userInputs: any,
    stepDetails: any[],
    TagList: string[]
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.UseCase,
      product: 'Loan',
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      doc_category: param.DocumentCategory,
      version: userInputs.Version,
      version_is_latest: userInputs.isLatest ?? true,
      version_tags: TagList.join(','),
    };
    return this.http.post(this.addVersionUrl, stepDetails, {
      headers,
      params: reqParams,
    });
  }

  /* Add Assest Class From Modal Add version */
  private _addAssetClassUrl =
    environment.apiUrl + 'loan-review/v1/add-asset-class-for-subproduct';

  addAsset(param: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      parent_usecase: param.UseCase,
      parent_product: 'Loan',
      parent_sub_product: param.sub_product,
      asset_class_name: param.asset_class,
    };
    return this.http.post(this._addAssetClassUrl, reqParams, {
      headers,
      params: reqParams,
    });
  }

  /* Add Sub Product */
  private _addSubProductURL =
    environment.apiUrl + 'loan-review/v1/add-sub-product-by-product';

  addSubProduct(param: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      parent_usecase: param.UseCase,
      parent_product: param.product,
      sub_product_name: param.subProduct,
    };
    return this.http.post(this._addSubProductURL, reqParams, {
      headers,
      params: reqParams,
    });
  }

  /* Add Product */
  private _addProductURL =
    environment.apiUrl + 'loan-review/v1/add-new-product';

  addProduct(param: any): Observable<any> {
    console.log(param);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: "Loan File Review",
      product_name: param.product,
    };
    return this.http.post(this._addProductURL, reqParams, {
      headers,
      params: reqParams,
    });
  }

  //instruction playground for admin rule  
  private getAttributeDescriptionUrl = environment.apiUrl + 'loan-review/auto-prompting/get-attribute-description';

  getAttributeDescription(attributeName: string): Observable<any> {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        accept: 'application/json',
      });
      const body = { attribute_name: attributeName };
      return this.http.post(this.getAttributeDescriptionUrl, body, { headers });
    }
  private getAttributeInstructionsUrl = environment.apiUrl + 'loan-review/auto-prompting/get-attribute-instructions';

  getAttributeInstructions(payload: {
    attribute_name: string;
    attribute_description: string;
    document_category: string;
    sample_ai_answer: string;
    sample_context: string;
    is_narrative_attribute: boolean;
  }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
    });
    return this.http.post(this.getAttributeInstructionsUrl, payload, { headers });
  }
  private getDocumentPrioritiesUrl = environment.apiUrl + 'loan-review/auto-prompting/get-document-priorities';

  getDocumentPriorities(payload: {
    attribute_name: string;
    attribute_description: string;
    sample_ai_answer: string;
    sample_context: string;
    is_narrative_attribute: boolean;
  }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
    });
    return this.http.post(this.getDocumentPrioritiesUrl, payload, { headers });
  }

  private getAllVersionTagsUrl = environment.apiUrl + 'loan-review/get-all-version-tags';

  getAllVersionTags(): Observable<any> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    return this.http.get(this.getAllVersionTagsUrl, { headers });
  }

  private updateInstructionLatestVersionUrl =
    environment.apiUrl + 'loan-review/v1/update-instruction-latest-version';

  updateInstructionLatestVersion(param: {
    usecase: string;
    product: string;
    sub_product: string;
    asset_class: string;
    data_element: string;
    doc_category: string;
    version: string;
    version_is_latest: boolean;
  }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
    const reqParams = {
      usecase: param.usecase,
      product: param.product,
      sub_product: param.sub_product,
      asset_class: param.asset_class,
      data_element: param.data_element,
      doc_category: param.doc_category,
      version: param.version,
      version_is_latest: param.version_is_latest,
    };
    return this.http.put(this.updateInstructionLatestVersionUrl, {}, {
      headers,
      params: reqParams,
    });
  }
}
